/**
 * PartnersPanel — Phase H (2026-09-21).
 *
 * Panneau admin « Partenaires » intégré à la fiche pratique canonique.
 * - Mini dashboard (compteurs par statut)
 * - Filtres (statut / catégorie / recherche texte)
 * - Liste des prospects, ajout, édition
 * - Bouton PRÉPARER L'EMAIL : brouillon template (jamais d'envoi, jamais de LLM)
 * - Section RELANCES À TRAITER
 *
 * SÉPARATION ÉDITORIALE : un prospect/partenaire ne devient JAMAIS automatiquement
 * un « coup de cœur » éditorial. Les marqueurs d'affichage public futur
 * (« PARTENAIRE — COMMUNICATION COMMERCIALE », « AVANTAGE ITERCATUS ») sont
 * structuraux uniquement, pas affichés publiquement (pre_launch_mode).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  listPartnerProspects,
  getPartnerProspectsDashboard,
  getPartnerProspectsRelances,
  createPartnerProspect,
  updatePartnerProspect,
  changePartnerProspectStatus,
  preparePartnerProspectEmail,
} from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  Handshake, Plus, Mail, AlertCircle, Pencil, Loader2, X, Check, RefreshCw,
} from "lucide-react";

const STATUSES = [
  "a_examiner", "valide", "rejete", "brouillon_pret", "contacte",
  "reponse_recue", "interesse", "a_negocier", "partenaire_actif",
  "refus", "a_relancer", "archive",
];

const STATUS_LABEL = {
  a_examiner: "À examiner",
  valide: "Validé",
  rejete: "Rejeté",
  brouillon_pret: "Brouillon prêt",
  contacte: "Contacté",
  reponse_recue: "Réponse reçue",
  interesse: "Intéressé",
  a_negocier: "À négocier",
  partenaire_actif: "Partenaire actif",
  refus: "Refus",
  a_relancer: "À relancer",
  archive: "Archivé",
};

const STATUS_COLOR = {
  a_examiner: "bg-slate-100 text-slate-800",
  valide: "bg-sky-100 text-sky-800",
  rejete: "bg-gray-100 text-gray-600",
  brouillon_pret: "bg-indigo-100 text-indigo-800",
  contacte: "bg-amber-100 text-amber-800",
  reponse_recue: "bg-teal-100 text-teal-800",
  interesse: "bg-purple-100 text-purple-800",
  a_negocier: "bg-orange-100 text-orange-800",
  partenaire_actif: "bg-emerald-100 text-emerald-800",
  refus: "bg-rose-100 text-rose-800",
  a_relancer: "bg-yellow-100 text-yellow-900",
  archive: "bg-neutral-100 text-neutral-700",
};

const CATEGORIES = [
  "restaurant", "hotel", "bar_cafe", "boutique", "excursion", "transport",
  "musee_site", "guide", "office_tourisme", "autre",
];

function emptyProspect(destinationId) {
  return {
    destination_id: destinationId,
    nom: "",
    categorie: "",
    adresse: "",
    ville: "",
    pays: "France",
    site_web: "",
    email: "",
    telephone: "",
    formulaire_contact: "",
    commentaire_interne: "",
    source: "",
    url_source: "",
    statut: "a_examiner",
    do_not_contact: false,
    members_only: false,
  };
}

function StatusBadge({ value }) {
  return (
    <span className={`inline-flex text-[11px] px-2 py-0.5 rounded ${STATUS_COLOR[value] || "bg-gray-100 text-gray-700"}`}>
      {STATUS_LABEL[value] || value}
    </span>
  );
}

function ProspectDialog({ open, onOpenChange, prospect, destinationId, onSaved }) {
  const [form, setForm] = useState(prospect || emptyProspect(destinationId));
  const [saving, setSaving] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null); // { subject, body }
  const [preparing, setPreparing] = useState(false);
  const [raison, setRaison] = useState("");

  useEffect(() => {
    setForm(prospect || emptyProspect(destinationId));
    setEmailDraft(null);
    setRaison("");
  }, [prospect, destinationId, open]);

  const isNew = !form?.id;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nom?.trim()) {
      toast.error("Le nom du partenaire est requis.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const r = await createPartnerProspect(form);
        toast.success("Prospect créé.");
        onSaved(r.data);
      } else {
        const patch = { ...form };
        delete patch.id;
        delete patch.destination_id;
        delete patch.created_at;
        delete patch.updated_at;
        delete patch.created_by;
        delete patch.statut;
        delete patch.status_history;
        const r = await updatePartnerProspect(form.id, patch);
        toast.success("Prospect mis à jour.");
        onSaved(r.data);
      }
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "inconnue"));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (next) => {
    if (!form.id) return;
    try {
      const r = await changePartnerProspectStatus(form.id, { statut: next });
      setForm(r.data);
      toast.success(`Statut → ${STATUS_LABEL[next] || next}`);
      onSaved(r.data);
    } catch (e) {
      toast.error("Erreur statut : " + (e?.response?.data?.detail || e?.message || "inconnue"));
    }
  };

  const prepareEmail = async () => {
    if (!form.id) {
      toast.error("Enregistrez d'abord le prospect.");
      return;
    }
    if (form.do_not_contact) {
      toast.error("Prospect marqué DO NOT CONTACT — préparation bloquée.");
      return;
    }
    setPreparing(true);
    try {
      const r = await preparePartnerProspectEmail(form.id, { raison_contact: raison || null });
      setEmailDraft({ subject: r.data.subject, body: r.data.body });
      toast.success("Brouillon généré (aucun email envoyé).");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "inconnue"));
    } finally {
      setPreparing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="prospect-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nouveau prospect" : (form.nom || "Prospect")}</DialogTitle>
          <DialogDescription>
            Rattachement à la fiche destination. Aucun email n'est envoyé automatiquement.
          </DialogDescription>
        </DialogHeader>

        {!isNew && (
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <StatusBadge value={form.statut} />
            <Select value={form.statut} onValueChange={changeStatus}>
              <SelectTrigger className="w-56 h-8" data-testid="prospect-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs font-medium">Nom *</label>
            <Input value={form.nom || ""} onChange={(e) => setField("nom", e.target.value)} data-testid="prospect-nom" />
          </div>
          <div>
            <label className="text-xs font-medium">Catégorie</label>
            <Select value={form.categorie || "none"} onValueChange={(v) => setField("categorie", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="prospect-categorie"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium">Adresse</label>
            <Input value={form.adresse || ""} onChange={(e) => setField("adresse", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Ville</label>
            <Input value={form.ville || ""} onChange={(e) => setField("ville", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Pays</label>
            <Input value={form.pays || ""} onChange={(e) => setField("pays", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Site web</label>
            <Input value={form.site_web || ""} onChange={(e) => setField("site_web", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Email</label>
            <Input value={form.email || ""} onChange={(e) => setField("email", e.target.value)} data-testid="prospect-email" />
          </div>
          <div>
            <label className="text-xs font-medium">Téléphone</label>
            <Input value={form.telephone || ""} onChange={(e) => setField("telephone", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Formulaire de contact (URL)</label>
            <Input value={form.formulaire_contact || ""} onChange={(e) => setField("formulaire_contact", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Source</label>
            <Input value={form.source || ""} onChange={(e) => setField("source", e.target.value)} placeholder="recommandation, visite, base locale…" />
          </div>
          <div>
            <label className="text-xs font-medium">URL source</label>
            <Input value={form.url_source || ""} onChange={(e) => setField("url_source", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium">Commentaire interne</label>
            <Textarea value={form.commentaire_interne || ""} onChange={(e) => setField("commentaire_interne", e.target.value)} rows={3} />
          </div>

          <div className="flex items-center justify-between border rounded-md px-3 py-2">
            <span className="text-sm">DO NOT CONTACT</span>
            <Switch checked={!!form.do_not_contact} onCheckedChange={(v) => setField("do_not_contact", v)} data-testid="prospect-dnc" />
          </div>
          <div className="flex items-center justify-between border rounded-md px-3 py-2">
            <span className="text-sm">Réservé adhérents (non activé)</span>
            <Switch checked={!!form.members_only} onCheckedChange={(v) => setField("members_only", v)} />
          </div>
        </div>

        {!isNew && (
          <div className="mt-6 border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-sm flex items-center gap-2"><Mail className="h-4 w-4" /> Préparer l'email (brouillon)</h3>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Aucun envoi</span>
            </div>
            <div className="space-y-2">
              <Input
                value={raison}
                onChange={(e) => setRaison(e.target.value)}
                placeholder="Courte raison de contact (optionnel)"
                data-testid="prospect-raison"
              />
              <Button variant="outline" size="sm" onClick={prepareEmail} disabled={preparing || form.do_not_contact} data-testid="prepare-email-btn">
                {preparing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Préparer l'email
              </Button>
              {form.do_not_contact && (
                <div className="text-xs text-rose-700 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Prospect marqué DO NOT CONTACT.
                </div>
              )}
              {emailDraft && (
                <div className="space-y-2 bg-muted/40 border rounded-md p-3" data-testid="email-draft-panel">
                  <div>
                    <label className="text-xs font-medium">Objet</label>
                    <Input value={emailDraft.subject} onChange={(e) => setEmailDraft((d) => ({ ...d, subject: e.target.value }))} data-testid="email-subject" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Corps du message</label>
                    <Textarea value={emailDraft.body} onChange={(e) => setEmailDraft((d) => ({ ...d, body: e.target.value }))} rows={12} data-testid="email-body" />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Modifiez librement. Aucun envoi automatique. L'envoi reste manuel (Gilles).
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button onClick={save} disabled={saving} data-testid="prospect-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PartnersPanel({ destinationId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [relances, setRelances] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categorieFilter, setCategorieFilter] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!destinationId) return;
    setLoading(true);
    try {
      const params = { destination_id: destinationId, limit: 200 };
      if (statusFilter !== "all") params.statut = statusFilter;
      if (categorieFilter !== "all") params.categorie = categorieFilter;
      if (q.trim()) params.q = q.trim();
      const [listR, dashR, relR] = await Promise.all([
        listPartnerProspects(params),
        getPartnerProspectsDashboard(destinationId),
        getPartnerProspectsRelances(destinationId),
      ]);
      setItems(listR.data?.items || []);
      setDashboard(dashR.data);
      setRelances(relR.data?.items || []);
    } catch (e) {
      toast.error("Erreur chargement partenaires : " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setLoading(false);
    }
  }, [destinationId, statusFilter, categorieFilter, q]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setOpen(true); };
  const onSaved = () => { setOpen(false); load(); };

  const counters = dashboard?.counters || {};

  const counterTiles = useMemo(() => ([
    { k: "a_examiner", label: "À examiner" },
    { k: "valide", label: "Validés" },
    { k: "contacte", label: "Contactés" },
    { k: "interesse", label: "Intéressés" },
    { k: "partenaire_actif", label: "Partenaires actifs" },
    { k: "a_relancer_total", label: "Relances à traiter", warn: true },
  ]), []);

  if (!destinationId) {
    return (
      <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
        La fiche doit être enregistrée au moins une fois pour gérer ses partenaires.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="partners-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Handshake className="h-5 w-5" />
          <h2 className="font-heading text-base">Prospection partenaires</h2>
          <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
            Pilote Marseille
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="partners-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="prospect-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Ajouter un prospect
          </Button>
        </div>
      </div>

      {/* Dashboard counters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="partners-dashboard">
        {counterTiles.map((t) => (
          <div
            key={t.k}
            className={`border rounded-md p-2 ${t.warn && (counters[t.k] || 0) > 0 ? "border-amber-400 bg-amber-50" : "border-border"}`}
            data-testid={`counter-${t.k}`}
          >
            <div className="text-[11px] text-muted-foreground">{t.label}</div>
            <div className="text-xl font-semibold">{counters[t.k] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="partners-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categorieFilter} onValueChange={setCategorieFilter}>
          <SelectTrigger className="w-44" data-testid="partners-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="Recherche (nom, ville, email…)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
          data-testid="partners-filter-q"
        />
      </div>

      {/* Prospects list */}
      <div className="border rounded-lg divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">
            Aucun prospect. Cliquez sur « Ajouter un prospect » pour commencer.
          </div>
        ) : items.map((p) => (
          <div
            key={p.id}
            className="p-3 flex items-center gap-3 hover:bg-muted/40 cursor-pointer"
            onClick={() => openEdit(p)}
            data-testid={`prospect-row-${p.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{p.nom}</div>
              <div className="text-xs text-muted-foreground truncate">
                {[p.categorie, p.ville, p.email].filter(Boolean).join(" · ")}
              </div>
            </div>
            <StatusBadge value={p.statut} />
            {p.do_not_contact && (
              <span className="text-[10px] px-1.5 py-0.5 bg-rose-100 text-rose-800 rounded">DNC</span>
            )}
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(p); }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      {/* Relances */}
      {relances.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="partners-relances">
          <h3 className="font-medium text-sm flex items-center gap-2 text-amber-900">
            <AlertCircle className="h-4 w-4" /> Relances à traiter ({relances.length})
          </h3>
          <div className="text-[11px] text-amber-900/80 mb-2">
            Contactés depuis plus de {dashboard?.delay_days || 10} jours sans réponse. Aucun email automatique n'est envoyé.
          </div>
          <ul className="divide-y divide-amber-200">
            {relances.map((p) => (
              <li
                key={p.id}
                className="py-1.5 text-sm flex items-center justify-between gap-2 cursor-pointer"
                onClick={() => openEdit(p)}
                data-testid={`relance-row-${p.id}`}
              >
                <span className="truncate">{p.nom} <span className="text-xs text-muted-foreground">({p.categorie || "—"})</span></span>
                <span className="text-xs text-amber-900">Contacté le {p.dernier_contact_at?.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ProspectDialog
        open={open}
        onOpenChange={setOpen}
        prospect={editing}
        destinationId={destinationId}
        onSaved={onSaved}
      />
    </div>
  );
}
